import { useEffect, useRef, useState } from 'react';
import { tx, UI, type Lang } from '../lib/i18n.ts';
import { Icon } from './Icon.tsx';

/**
 * 第三方登录的两个界面零件：Google 的标志、Telegram 的登录按钮。
 *
 * 放在一处是因为**两个地方要用**：登录页（还没登录，用来登进来）
 * 和外观页的「账号与安全」（已经登录，用来把渠道绑到当前账号上）。
 * 各抄一份的结果一定是某天只改了一边 —— 而这两处的失败方式还不一样，
 * 一边是「登不进来」，一边是「绑不上」。
 */

/**
 * Google 的 G。
 *
 * 用官方那四个颜色，不用我们自己的图标集 —— Google 的品牌规范要求
 * 「用 Google 登录」的按钮上必须是他们的标志，换成一个单色的通用图标
 * 既不合规，也会让人怀疑这个按钮是不是真的 Google。
 * `aria-hidden`：旁边的文字已经说清是什么了，读屏不用再念一遍。
 */
export function GoogleMark(): React.ReactElement {
  return (
    <svg className="gmark" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H1.05v2.34A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H1.05a9 9 0 0 0 0 8.1l2.92-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A8.997 8.997 0 0 0 1.05 4.95l2.92 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/**
 * Telegram 的登录按钮。
 *
 * ════════════════════════════════════════════════════════
 * 它不是我们画的按钮，是 telegram.org 塞进来的一个 iframe
 * ════════════════════════════════════════════════════════
 * Telegram 不走 OAuth：官方做法是往页面里插一个 `<script>`，
 * 那个脚本再插一个来自 `oauth.telegram.org` 的 iframe，
 * 用户在里面授权完之后回调页面上的一个**全局函数**。
 *
 * 由此来的三个实际问题，每一个都要在界面上有交代：
 *
 * 1. **国内直连大概率加载不出来。** telegram.org 被墙，脚本请求会失败。
 *    失败时 DOM 里什么都没有 —— 如果不管，用户看到的是一块空白，
 *    完全不知道发生了什么。
 *
 * 2. **BotFather 里必须 /setdomain。** 没设的话 iframe 照样出来，
 *    但里面只有一行白底黑字「Bot domain invalid」（本地实测过）——
 *    登录页正中一块白框。服务器会定期去问 Telegram，没设好就干脆不下发这个按钮
 *    （见 apps/api 的 telegram-login-probe.ts），这里是第二道：
 *
 *    **判断「按钮真的活了」靠的是 iframe 发来的消息**，不是有没有 iframe。
 *    正常的那一页一加载就会通过 postMessage 报自己的尺寸；
 *    「Bot domain invalid」那一页是纯文本，什么都不会发。
 *    所以 6 秒内没收到 oauth.telegram.org 的消息，就把 iframe 收起来，换成一句人话 ——
 *    两种失败（被墙、域名没设）都走这一条。
 *
 * 3. **回调只能是全局函数名。** `data-onauth` 收的是一段字符串
 *    （`jyfkTelegramAuth(user)`），所以必须往 window 上挂一个函数。
 *    挂的是一个**固定的转发函数**，真正的处理器放在 ref 里 ——
 *    直接挂闭包的话，语言一切换、组件一重渲染，挂上去的就是旧闭包，
 *    而它捕获的跳转目标是旧的。
 */
export function TelegramButton(
  { bot, lang, onAuth, requestWrite = true, note, stuckText }: {
    bot: string;
    lang: Lang;
    onAuth: (payload: Record<string, unknown>) => void;
    /**
     * 顺便要「可以给你发消息」的权限。
     *
     * 登录页上要（默认 true）：拿到它之后风控提醒可以直接发到用户的 Telegram，
     * 不用再走一遍「先去给机器人发句话」的绑定流程 —— 那一步最容易卡住人。
     * 不给也照样能登录 / 绑定。
     */
    requestWrite?: boolean;
    /** 按钮下面那句说明。不传就用登录页那句 */
    note?: string;
    /**
     * 按钮没活的时候那句话。不传就用登录页那句（它会说「也可以先用邮箱进来」——
     * 在已经登录了的绑定卡片上这句话是错的，所以那里要传自己的）
     */
    stuckText?: string;
  }
): React.ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const handler = useRef(onAuth);
  handler.current = onAuth;
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    setStuck(false);
    const w = window as unknown as Record<string, unknown>;
    w.jyfkTelegramAuth = (u: Record<string, unknown>) => { handler.current(u); };

    /* 按钮活了没有：看 oauth.telegram.org 有没有给我们发过消息（理由见上面第 2 条） */
    let alive = false;
    const onMessage = (ev: MessageEvent): void => {
      if (ev.origin === 'https://oauth.telegram.org') alive = true;
    };
    window.addEventListener('message', onMessage);

    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://telegram.org/js/telegram-widget.js?22';
    s.setAttribute('data-telegram-login', bot);
    s.setAttribute('data-size', 'large');
    s.setAttribute('data-radius', '10');
    if (requestWrite) s.setAttribute('data-request-access', 'write');
    s.setAttribute('data-onauth', 'jyfkTelegramAuth(user)');
    /* 只在英文界面显式指定语言。其余情况交给它自己按浏览器判断 ——
       Telegram 的语言代码和我们的不一样（它认 zh-hans 之类），
       猜错了会让组件直接不渲染，而那个代价远大于按钮上的字是英文 */
    if (lang === 'en') s.setAttribute('data-lang', 'en');
    s.onerror = () => setStuck(true);
    el.appendChild(s);

    /* 6 秒还没收到它的消息就当没活。
       **不能用「有没有 iframe」判断**：域名没设的时候 iframe 照样会出来，
       里面是那行「Bot domain invalid」；也不能用脚本的 onload，那种情况它是正常触发的 */
    const timer = setTimeout(() => {
      if (!alive) setStuck(true);
    }, 6000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      delete w.jyfkTelegramAuth;
      el.replaceChildren();
    };
  }, [bot, lang, requestWrite]);

  return (
    <div className="tg-login">
      {/* 没活的时候把 iframe 收起来（不删，删了它就再也活不过来了）——
          留着那块白框只会让人以为页面坏了 */}
      <div ref={box} className="tg-slot" hidden={stuck} />
      {stuck ? (
        <div className="banner info sm" role="status">
          <span className="ic"><Icon name="info" /></span>
          <span>{stuckText ?? tx(UI.tgWidgetStuck, lang)}</span>
        </div>
      ) : (
        <span className="foot">{note ?? tx(UI.tgNote, lang)}</span>
      )}
    </div>
  );
}
